/**
 * Pure helpers for editing an officer's availability windows from the roster
 * grid's inline "availability layer" (see OfficerScheduleGrid).
 *
 * Availability windows are the single source of truth stored on
 * officer_metadata.availability_segments (or availability_start/end for a
 * single window) — these helpers only compute what the next value of that
 * list should be; callers are responsible for persisting it via
 * apiClient.updateAvailability and re-rendering from the refreshed metadata.
 */

import { hmmmToSlot, hmmmToEndSlot } from '@/config/modeConfig';
import { slotToHhmm } from '@/utils/partialTiming';

export interface AvailWindow {
  start: string;
  end: string;
}

/**
 * Sort windows chronologically and fuse any that genuinely overlap into one.
 *
 * Deliberately does NOT merge windows that merely touch (one's end === the
 * next's start, no gap and no overlap) — that exact shape is what splitting a
 * window into two produces, and is also what adding a new window next to an
 * existing one clips down to so it doesn't overlap (see addWindowAt). Both
 * must survive as two distinct windows. Only a real overlap (one's end slot
 * strictly past the next's start — e.g. dragging an end time past the next
 * window's start) is the "overlapping availability window" this merges.
 */
export function mergeAvailabilitySegments(segs: AvailWindow[], startHour: number): AvailWindow[] {
  const withSlots = segs
    .map(s => {
      try {
        return { ss: hmmmToSlot(s.start, startHour), se: hmmmToEndSlot(s.end, startHour) };
      } catch {
        return null;
      }
    })
    .filter((s): s is { ss: number; se: number } => s !== null && s.se > s.ss)
    .sort((a, b) => a.ss - b.ss);

  const merged: { ss: number; se: number }[] = [];
  for (const s of withSlots) {
    const last = merged[merged.length - 1];
    if (last && s.ss < last.se) {
      last.se = Math.max(last.se, s.se);
    } else {
      merged.push({ ...s });
    }
  }

  return merged.map(m => ({ start: slotToHhmm(m.ss, startHour), end: slotToHhmm(m.se, startHour) }));
}

/**
 * Widen the availability window nearest a new counter row that lands outside
 * every existing window, so the row lands inside one instead of being turned
 * away — no separate window is created, and the caller never has to ask.
 *
 * Prefers the window ending soonest before `newStart` (the common case: the
 * last row in a window ran out, and the new one picks up right after) and
 * extends its end to `newEnd`. If nothing ends before `newStart` (the gap is
 * before every window — e.g. the very first row), the window starting soonest
 * after is pulled back to `newStart` instead. Existing windows are never
 * shrunk, only grown; `mergeAvailabilitySegments` then fuses the result if
 * the widened window now reaches into (or past) a neighbour.
 *
 * Returns `segs` unchanged if there is nothing to widen — no windows at all,
 * or `newStart`/`newEnd` fail to parse.
 */
export function widenWindowsToCover(
  segs: AvailWindow[],
  newStart: string,
  newEnd: string,
  startHour: number,
): AvailWindow[] {
  if (segs.length === 0) return segs;
  let newStartSlot: number, newEndSlot: number;
  try {
    newStartSlot = hmmmToSlot(newStart, startHour);
    newEndSlot = hmmmToEndSlot(newEnd, startHour);
  } catch {
    return segs;
  }

  let precedingIdx = -1, precedingEnd = -Infinity;
  let followingIdx = -1, followingStart = Infinity;
  segs.forEach((seg, i) => {
    try {
      const se = hmmmToEndSlot(seg.end, startHour);
      const ss = hmmmToSlot(seg.start, startHour);
      if (se <= newStartSlot && se > precedingEnd) { precedingIdx = i; precedingEnd = se; }
      if (ss >= newStartSlot && ss < followingStart) { followingIdx = i; followingStart = ss; }
    } catch { /* skip an unparsable window rather than aborting the whole scan */ }
  });

  if (precedingIdx >= 0) {
    // Never shrink: only widen the end forward, even if a caller somehow
    // passes a newEnd earlier than what's already there.
    const end = newEndSlot > precedingEnd ? newEnd : segs[precedingIdx].end;
    return mergeAvailabilitySegments(
      segs.map((s, i) => i === precedingIdx ? { ...s, end } : s), startHour,
    );
  }
  if (followingIdx >= 0) {
    const seg = segs[followingIdx];
    const segEndSlot = (() => { try { return hmmmToEndSlot(seg.end, startHour); } catch { return -Infinity; } })();
    const start = newStartSlot < followingStart ? newStart : seg.start;
    const end = newEndSlot > segEndSlot ? newEnd : seg.end;
    return mergeAvailabilitySegments(
      segs.map((s, i) => i === followingIdx ? { ...s, start, end } : s), startHour,
    );
  }
  return segs;
}


/** A row of an officer's day, by its HHMM range (an AssignmentRow, an allocation, …). */
export interface TimedRow {
  startTime: string;
  endTime: string;
}

const slotsOf = (start: string, end: string, startHour: number): [number, number] | null => {
  try {
    const s = hmmmToSlot(start, startHour);
    const e = hmmmToEndSlot(end, startHour);
    return e > s ? [s, e] : null;
  } catch { return null; }
};

/**
 * The windows widened — never narrowed — so that every row lies inside one: a row running past the end of
 * the window it starts in pushes that end out, and a row outside every window widens the nearest one
 * (widenWindowsToCover). This is the rule for an officer's day everywhere it is shown: adding a counter
 * grows the window, and taking one away or shortening it leaves the window as it was.
 */
export function windowsCoveringRows(segs: AvailWindow[], rows: TimedRow[], startHour: number): AvailWindow[] {
  let out = mergeAvailabilitySegments(segs, startHour);
  for (const row of rows) {
    const r = slotsOf(row.startTime, row.endTime, startHour);
    if (!r) continue;
    if (out.length === 0) { out = [{ start: row.startTime, end: row.endTime }]; continue; }
    const host = out.findIndex(w => { const ws = slotsOf(w.start, w.end, startHour); return !!ws && r[0] >= ws[0] && r[0] < ws[1]; });
    if (host < 0) { out = widenWindowsToCover(out, row.startTime, row.endTime, startHour); continue; }
    const hs = slotsOf(out[host].start, out[host].end, startHour);
    if (hs && r[1] > hs[1]) {
      out = mergeAvailabilitySegments(out.map((w, i) => (i === host ? { ...w, end: row.endTime } : w)), startHour);
    }
  }
  return out;
}

/** The rows that belong to `window`: those starting inside it. */
export function rowsInWindow<R extends TimedRow>(rows: R[], window: AvailWindow, startHour: number): R[] {
  const ws = slotsOf(window.start, window.end, startHour);
  if (!ws) return [];
  return rows.filter(row => {
    try { const s = hmmmToSlot(row.startTime, startHour); return s >= ws[0] && s < ws[1]; } catch { return false; }
  });
}

/** What an officer's metadata says about when they are on (OfficerMetadata's window fields). */
export interface WindowedMeta {
  availability_start?: string | null;
  availability_end?: string | null;
  availability_segments?: Array<{ start: string; end: string }> | null;
}

/** An officer's availability windows: every segment of a multi-window officer, else the single start/end
 *  window, else none. The one reading of the metadata every grid and card shares. */
export function officerWindows(meta: WindowedMeta | undefined): AvailWindow[] {
  if (meta?.availability_segments && meta.availability_segments.length > 1) {
    return meta.availability_segments.map(s => ({ start: s.start, end: s.end }));
  }
  if (meta?.availability_start && meta?.availability_end) {
    return [{ start: meta.availability_start, end: meta.availability_end }];
  }
  return [];
}

/** Whether `slot` falls inside one of `windows`. */
export function slotInWindows(slot: number, windows: AvailWindow[], startHour: number): boolean {
  return windows.some(w => { const ws = slotsOf(w.start, w.end, startHour); return !!ws && slot >= ws[0] && slot < ws[1]; });
}

/** "1000-1300, 1500-2200" — the windows as an officer reads them; "no availability window" for none. */
export function windowsText(windows: AvailWindow[]): string {
  return windows.length > 0 ? windows.map(w => `${w.start}-${w.end}`).join(', ') : 'no availability window';
}
